// Engine-free access to the browser wallet's stored key, for the two moments the store cannot wait for the engine
// chunk: the flow resolution reads the held ADDRESS synchronously at start-up (a held key is the identity, plan §4.3),
// and Sign out must wipe the key synchronously even for a V1 user whose page never loaded the engine.
//
// The stored key belongs to the vendored keys shim (`@snappy/core/lib/wallet/keys`): this file never reads a phrase
// and never writes a key. Both read it through the one strict parser of `./vault/envelope` (no crypto, no engine).
// `keyRecordPeek.test.ts` pins the storage key to the shim's `KEY_RECORD_STORAGE_KEY`.

import { parseStoredKey, storedKeyAddress } from './vault/envelope';

/** Same value as `KEY_RECORD_STORAGE_KEY` in `@snappy/core/lib/wallet/keys` (importing it would pull in the engine). */
export const KEY_RECORD_KEY = 'snappy.browserWallet.key.v1';

/** How the held key is protected: a PIN or passkey envelope (`v: 2`), or a legacy clear record (`v: 1`). */
export type HeldProtection = 'pin' | 'passkey' | 'legacy';

function storage(): Storage | null {
    try {
        return globalThis.localStorage ?? null;
    } catch {
        return null;
    }
}

function peekStored() {
    try {
        return parseStoredKey(storage()?.getItem(KEY_RECORD_KEY) ?? null);
    } catch {
        return null;
    }
}

/**
 * The address of the wallet this browser holds (sealed or not), or null. The address is in clear in both shapes; the
 * engine's shim is the authority once loaded.
 */
export function peekHeldAddress(): `0x${string}` | null {
    const stored = peekStored();
    return stored ? storedKeyAddress(stored) : null;
}

/** How the held key is protected, or null when no wallet is held. */
export function peekHeldProtection(): HeldProtection | null {
    const stored = peekStored();
    if (!stored) return null;
    return stored.kind === 'envelope' ? stored.envelope.method : 'legacy';
}

/** Removes the stored key from this browser without the engine (its listeners are then notified by the caller). */
export function wipeKeyRecordNow(): void {
    try {
        storage()?.removeItem(KEY_RECORD_KEY);
    } catch {
        // storage blocked: there is nothing this page could have written either.
    }
}
